// src/lib/authFetch.ts
// Interceptor global sencillo: adjunta Authorization si hay token y, ante 401, limpia sesión y redirige a /login.

import { getJwt, getSessionUser, redirectToLogin } from './session';
import { normalizeRole } from '@/auth/permissions';
import { getVendorContextHeaderValue } from './vendorContext';

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

const PATCHED_FLAG = '__auth_fetch_patched__';
const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/me'];
const ALMACEN_ID_KEY = 'inv:almacenId';
const ALMACEN_CODE_KEY = 'inventario:almacen';
const ALMACEN_MAP_KEY = 'inv:almacenMap';

const isAuthEndpoint = (url?: string | null) => {
  if (!url) return false;
  const normalized = url.toString();
  if (AUTH_ENDPOINTS.some(endpoint => normalized.includes(endpoint))) {
    return true;
  }
  return ['/auth/login', '/auth/refresh', '/auth/me'].some(fragment => normalized.includes(fragment));
};

const shouldLogoutOn401 = (url?: string | null) => {
  if (!url) return true;
  return !url.toString().includes('/auth/login');
};

const readStoredAlmacenId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ALMACEN_ID_KEY);
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? String(num) : null;
  } catch {
    return null;
  }
};

const readStoredAlmacenFromCode = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const code = window.localStorage.getItem(ALMACEN_CODE_KEY);
    if (!code) return null;
    const rawMap = window.localStorage.getItem(ALMACEN_MAP_KEY);
    if (!rawMap) return null;
    const map = JSON.parse(rawMap) as Record<string, number>;
    const id = map[code.trim().toUpperCase()];
    return Number.isFinite(id) && id > 0 ? String(id) : null;
  } catch {
    return null;
  }
};

const resolveAdminAlmacenId = (): string | null => {
  return readStoredAlmacenId() || readStoredAlmacenFromCode();
};

export function applyAuthFetch() {
  if (typeof window === 'undefined') return;
  if ((window as any)[PATCHED_FLAG]) return; // prevent double patching

  const original = window.fetch.bind(window);
  (window as any)[PATCHED_FLAG] = true;

  window.fetch = async (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const firstInit: RequestInit = { ...(init ?? {}) };

    const headers = new Headers(firstInit.headers || (input instanceof Request ? input.headers : undefined));
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
    const isAuthRequest = isAuthEndpoint(url);
    const jwt = getJwt();
    const user = getSessionUser();

    if (jwt && !isAuthRequest && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${jwt}`);
    }
    if (!isAuthRequest) {
      const role = normalizeRole(user?.role ?? null);
      if (role === 'ADMIN' && !headers.has('X-Almacen-Id')) {
        const almacenId = resolveAdminAlmacenId();
        if (almacenId) headers.set('X-Almacen-Id', almacenId);
      }
    }
    const vendorHeader = getVendorContextHeaderValue();
    if (vendorHeader && !headers.has('X-Vendor-Id')) {
      headers.set('X-Vendor-Id', vendorHeader);
    }
    firstInit.headers = headers;

    const resp = await original(input, firstInit);
    if (resp.status === 401 && shouldLogoutOn401(url)) {
      let payload: any = null;
      try {
        payload = await resp.clone().json();
      } catch {
        payload = null;
      }
      redirectToLogin({
        reason: payload?.code === 'SESSION_REVOKED' ? 'session_revoked' : 'session_expired',
      });
    }
    return resp;
  };
}

export async function authFetchBlob(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const firstInit: RequestInit = { ...init };
  const headers = new Headers(firstInit.headers || (input instanceof Request ? input.headers : undefined));
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
  const isAuthRequest = isAuthEndpoint(url);
  const jwt = getJwt();
  const user = getSessionUser();

  if (jwt && !isAuthRequest && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${jwt}`);
  }
  if (!isAuthRequest) {
    const role = normalizeRole(user?.role ?? null);
    if (role === 'ADMIN' && !headers.has('X-Almacen-Id')) {
      const almacenId = resolveAdminAlmacenId();
      if (almacenId) headers.set('X-Almacen-Id', almacenId);
    }
  }
  const vendorHeader = getVendorContextHeaderValue();
  if (vendorHeader && !headers.has('X-Vendor-Id')) {
    headers.set('X-Vendor-Id', vendorHeader);
  }
  firstInit.headers = headers;

  const original = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;
  const resp = await original(input, firstInit);
  if (resp.status === 401 && shouldLogoutOn401(url)) {
    let payload: any = null;
    try {
      payload = await resp.clone().json();
    } catch {
      payload = null;
    }
    redirectToLogin({
      reason: payload?.code === 'SESSION_REVOKED' ? 'session_revoked' : 'session_expired',
    });
  }
  return resp;
}
